import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  linkedSignal,
  model,
  output,
  viewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import type { FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { getRandomId } from '../../../utils/get-random-id';
import { InputSizeType, InputType } from '../../../types';
import { DropdownUIComponent } from '../../atoms/dropdown/dropdown.component';
import { IconUIComponent } from '../../atoms/icon/icon.component';
import { InputFieldComponent } from '../../atoms/input-field/input-field.component';
import { ValidationErrorUIComponent } from '../../atoms/validation-error/validation-error.component';
import { AutocompleteContentUIComponent } from './autocomplete-content/autocomplete-content.component';
import { AutocompletePostfixIconDirective } from './directives/autocomplete-postfix-icon.directive';
import { AutocompleteValueDirective } from './directives/autocomplete-value.directive';

/**
 * The value is either a picked item (rendered through the `uiAutocompleteValue` template) or,
 * for free-text lookups, the typed string. Typing clears the picked item and emits
 * `autocompleteSearch`; the caller loads matches and renders them as `fr8bot-ui-autocomplete-item`.
 * Opening the list with an empty input emits an empty search, so the caller shows its first page.
 * The list shows nine items and emits `autocompleteScrollEnd` near its end; `injectAutocompletePaging`
 * implements the caller's side of both.
 */
@Component({
  selector: 'fr8bot-ui-autocomplete-field',
  imports: [
    AutocompleteContentUIComponent,
    CdkOverlayOrigin,
    DropdownUIComponent,
    IconUIComponent,
    InputFieldComponent,
    NgTemplateOutlet,
    ValidationErrorUIComponent,
  ],
  templateUrl: './autocomplete-field.component.html',
  styleUrl: './autocomplete-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteFieldComponent<ValueType, ItemType = ValueType>
  extends UiFieldControl
  implements FormValueControl<ValueType | null>
{
  readonly value = model<ValueType | null>(null);

  readonly label = input<string | null>(null);
  readonly placeholder = input<string | null>(null);
  readonly size = input<InputSizeType>('large');
  readonly type = input<InputType>('text');
  /** Shows a spinner under the list while the host fetches the next page. */
  readonly loadingMore = input(false);

  readonly autocompleteSearch = output<string>();
  /** The list is scrolled close to its end; the host loads the next page, if there is one. */
  readonly autocompleteScrollEnd = output<void>();

  protected readonly postfixIconRef = contentChild(AutocompletePostfixIconDirective);
  protected readonly valueTemplate = contentChild(AutocompleteValueDirective);
  protected readonly dropdown = viewChild(DropdownUIComponent);
  private readonly content = viewChild.required(AutocompleteContentUIComponent);

  protected readonly id = getRandomId('autocomplete');

  /** An object value is a picked item; a string value is what the user typed. */
  protected readonly selectedItem = linkedSignal<ItemType | null>(() => {
    const value = this.value();
    return value !== null && typeof value === 'object' ? (value as unknown as ItemType) : null;
  });

  protected readonly searchText = linkedSignal<string>(() => {
    const value = this.value();
    return typeof value === 'string' ? value : '';
  });

  protected readonly hasValue = computed(() => this.value() !== null && this.value() !== undefined);

  protected onSearch(text: string | number | null): void {
    const search = text === null ? '' : String(text);

    if (search === this.value()) return;

    // Clearing the value re-derives the text from it, so the typed text is written back after.
    this.value.set(null);
    this.searchText.set(search);
    this.content().scrollToTop();
    this.autocompleteSearch.emit(search);
  }

  // With nothing typed yet no search has run, so the list would open empty until the user types.
  protected onOpened(): void {
    if (this.disabled() || this.selectedItem() || this.searchText()) return;

    this.autocompleteSearch.emit('');
  }

  selectValue(value: ValueType): void {
    this.value.set(value);
    this.dropdown()?.close();
  }

  selectItem(item: ItemType): void {
    this.selectedItem.set(item);
    this.searchText.set('');
  }

  protected clearValue(): void {
    if (this.disabled()) return;

    this.value.set(null);
    this.touch.emit();
  }
}
